import {
	booleanAttribute,
	Component,
	Input,
	ChangeDetectionStrategy,
} from "@angular/core"
import { COMMON_IMPORTS } from "../common-imports"
import { byId, getIds } from "@app/helpers"
import { ApiService } from "@app/services/api.service"
import { LangService } from "@app/services/lang.service"
import { ModalService } from "@app/services/modal.service"
import { StoreService } from "@app/services/store.service"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { AddInvoiceComponent } from "@app/add-invoice/add-invoice.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { UserDocumentsComponent } from "@app/user-documents/user-documents.component"
import { ProductPersonsComponent } from "@app/product-persons/product-persons.component"
import { SubUserPatientsComponent } from "@app/sub-user-patients/sub-user-patients.component"
import { FollowupsUpdateListComponent } from "@app/followups-update-list/followups-update-list.component"
import { FinresourceTranslationComponent } from "@app/finresource-translation/finresource-translation.component"
import { FinresourceCreditComponent } from "@app/finresource-credit/finresource-credit.component"
import { PreviewFinresourceComponent } from "@app/preview-finresource/preview-finresource.component"
import { TableNameDisplayPipe } from "@app/utils/table-name-display.pipe"
import { GenTableDisplayComponent } from "@app/gen-table-display/gen-table-display.component"
import { TaskComponent } from "@app/task/task.component"
import { PatientComponent } from "@app/patient/patient.component"

@Component({
	selector: "app-gen-table-field-btn",
	imports: [
		COMMON_IMPORTS,
		LangValPipe,
		AddInvoiceComponent,
		ModalWrapComponent,
		UserDocumentsComponent,
		ProductPersonsComponent,
		SubUserPatientsComponent,
		FollowupsUpdateListComponent,
		FinresourceTranslationComponent,
		FinresourceCreditComponent,
		PreviewFinresourceComponent,
		TableNameDisplayPipe,
		GenTableDisplayComponent,
		TaskComponent,
		PatientComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (isV2) {
			<button
				class="btn btn-default"
				(click)="open()"
				[preIcon]="attrs.icon"
				[isImg]="attrs.isImg"
				[titleLang]="$safeNavigationMigration(attrs?.title)"
			>
				@if (attrs?.suffixText) {
					<span class="showtext"> {{ attrs.suffixText | langVal }}</span>
				}
			</button>
		} @else {
			<button
				class="btn btn-icon"
				(click)="open()"
			>
				<app-icon
					[icon]="attrs.icon"
					[isImg]="attrs.isImg"
				/>
			</button>
		}

		@switch (modalProps?.name) {
			@case ("add-invoice") {
				<app-add-invoice
					(onClose)="modalProps = null"
					[description]="modalProps.description"
					[doctype]="modalProps.doctype"
					[resource]="modalProps.resource"
					[inputRow]="modalProps.inputRow"
				/>
			}
			@case ("user-documents") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					modalWrapperClass="modal-wide"
					hasExit
				>
					<h3 header>
						{{ "documents" | langVal }}
						- {{ row?.name }}
					</h3>
					<app-user-documents
						(onClose)="modalProps = null"
						[objectRow]="row"
					/>
				</app-modal-wrap>
			}
			@case ("product-persons") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					title="product_persons"
					modalWrapperClass="modal-800"
					hasExit
				>
					<app-product-persons
						(onClose)="modalProps = null"
						[objectRow]="row"
					/>
				</app-modal-wrap>
			}
			@case ("sub-user-patients") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					title="patients"
				>
					<app-sub-user-patients
						(onClose)="modalProps = null"
						isModalMode
						[inputRow]="row"
					/>
				</app-modal-wrap>
			}
			@case ("followups-update-list") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					title="followups"
				>
					<app-followups-update-list
						(onClose)="modalProps = null"
						filterField="user_id"
						[objectRow]="row"
					/>
				</app-modal-wrap>
			}
			@case ("finresource-translation") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					title="text_to_translate_in_english_invoice"
				>
					<app-finresource-translation
						(onClose)="modalProps = null"
						[objectRow]="row"
					/>
				</app-modal-wrap>
			}
			@case ("finresource-credit") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					title="credit_invoice"
					modalWrapperClass="modal-650"
				>
					<app-finresource-credit
						(onClose)="modalProps = null"
						[objectRow]="row"
					/>
				</app-modal-wrap>
			}
			@case ("preview-finresource") {
				<app-preview-finresource
					(onClose)="modalProps = null"
					isDemoMode
					[headtureRow]="row"
				/>
			}
			@case ("gen-table-display") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					[title]="modalProps.tableName | tableNameDisplay"
					modalWrapperClass="modal-wide"
				>
					<app-gen-table-display
						(onClose)="modalProps = null"
						[tableName]="modalProps.tableName"
						[filterField]="modalProps.filterField"
						[objectRow]="row"
						[ignoreError]="modalProps.ignoreError"
					/>
				</app-modal-wrap>
			}
			@case ("task") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					modalWrapperClass="modal-650"
					title="tasks"
				>
					<app-task
						(onClose)="modalProps = null"
						[patientRow]="row"
					/>
				</app-modal-wrap>
			}
			@case ("patient") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					[title]="modalProps.patient.name"
					modalWrapperClass="modal-1200"
				>
					<app-patient
						(onClose)="modalProps = null"
						[patient]="modalProps.patient"
					/>
				</app-modal-wrap>
			}
		}
	`,
})
export class GenTableFieldBtnComponent {
	modalProps: any = null
	@Input({ transform: booleanAttribute }) isV2: boolean = false
	@Input() attrs: any = null
	@Input() row: any = null
	@Input() tableName: string = ""

	constructor(
		public store: StoreService,
		public modalService: ModalService
	) {}

	async open() {
		const { fieldsToUpdate, modal } = this.attrs
		if (modal === "add-invoice" && fieldsToUpdate.resource === "patient") {
			const isGroupPatients = this.tableName === "grouppatients"
			const patient =
				isGroupPatients &&
				this.store.getSiteDataTable("patients").find(byId(this.row.patient_id))

			const description = this.store.getAddInvoiceDescriptionDefault(
				patient?.name || this.row.name,
				patient?.id || this.row.id
			)
			const inputRow = patient || this.row
			this.modalProps = {
				name: "add-invoice",
				description,
				inputRow,
				resource: fieldsToUpdate.resource,
				doctype: fieldsToUpdate.doctype,
			}
			return
		}

		if (modal === "patient" && this.tableName === "grouppatients") {
			const patient = await this.store.getPatient(this.row.patient_id)
			this.modalProps = { name: "patient", patient }
			return
		}

		switch (modal) {
			case "add-invoice": {
				this.modalProps = {
					name: "add-invoice",
					inputRow: this.row,
					resource: fieldsToUpdate.resource,
					doctype: fieldsToUpdate.doctype,
				}
				return
			}
			case "product-persons":
			case "task":
			case "preview-finresource":
			case "finresource-credit":
			case "finresource-translation":
			case "followups-update-list":
			case "sub-user-patients":
			case "user-documents": {
				this.modalProps = { name: modal }
				return
			}
			case "gen-table-display": {
				this.modalProps = {
					name: modal,
					tableName: fieldsToUpdate.tableName,
					filterField: fieldsToUpdate.filterField,
					ignoreError: fieldsToUpdate.ignoreError,
				}
				return
			}
		}
	}
}
